import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowRight, History, Minus, Plus, Shuffle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import { buildReactivationDiff } from '../../utils/snapshotDiff';
import { rollbackEntityVersion } from '../../utils/api';

const LIST_CAP = 8;

// Find the current snapshot among an entity's versions.
function currentSnapshotOf(versions) {
  return versions?.find((v) => v.isCurrent)?.fullSnapshot ?? null;
}

export default function ReactivateModal({ open, onClose, entity, versions, target, onDone }) {
  const [submitting, setSubmitting] = useState(false);

  const currentVersion = useMemo(() => versions?.find((v) => v.isCurrent), [versions]);
  const diff = useMemo(() => {
    if (!entity || !target) return null;
    return buildReactivationDiff(
      entity.entityType,
      currentSnapshotOf(versions),
      target.fullSnapshot
    );
  }, [entity, target, versions]);

  if (!open || !entity || !target) return null;

  const isRoute = entity.entityType === 'route';

  async function confirm() {
    setSubmitting(true);
    try {
      const response = await rollbackEntityVersion(entity.entityRef, target.id);
      toast.success(`Reactivated V${response.data.reactivatedVersionNumber}`);
      onDone?.(response.data);
      onClose?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Reactivation failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={submitting ? undefined : onClose}
      title="Reactivate version"
      description={`${entity.label} · ${entity.name}`}
      icon={History}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant="warning" onClick={confirm} loading={submitting} rightIcon={History}>
            Reactivate V{target.versionNumber}
          </Button>
        </>
      }
    >
      {/* Current vs After */}
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
        <SnapshotCard
          tone="current"
          title="Current"
          version={currentVersion?.versionNumber}
          count={diff?.currentCount}
          isRoute={isRoute}
        />
        <ArrowRight className="h-5 w-5 text-slate-400" />
        <SnapshotCard
          tone="after"
          title="After reactivation"
          version={target.versionNumber}
          count={diff?.afterCount}
          isRoute={isRoute}
        />
      </div>

      {!diff?.hasChanges ? (
        <p className="mt-4 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 text-[13px] text-slate-500">
          This version is identical to the current state. Reactivating will simply re-stamp it
          as current.
        </p>
      ) : (
        <div className="mt-4 space-y-3">
          {diff.metaChanges.length > 0 && (
            <Section title="Field changes">
              {diff.metaChanges.map((change) => (
                <li key={change.field} className="flex items-center gap-2 text-[12.5px]">
                  <span className="font-medium text-slate-700">{change.field}:</span>
                  <span className="text-slate-400 line-through truncate">{String(change.from)}</span>
                  <ArrowRight className="h-3 w-3 text-slate-300 shrink-0" />
                  <span className="text-slate-900 font-medium truncate">{String(change.to)}</span>
                </li>
              ))}
            </Section>
          )}

          {isRoute && diff.itemsAdded.length > 0 && (
            <Section title="Stops added back" icon={Plus} tone="success">
              <CappedList
                items={diff.itemsAdded}
                render={(item) => `#${item.sequence ?? '?'} ${item.name || item.id}`}
              />
            </Section>
          )}

          {isRoute && diff.itemsRemoved.length > 0 && (
            <Section title="Stops removed" icon={Minus} tone="danger">
              <CappedList
                items={diff.itemsRemoved}
                render={(item) => `#${item.sequence ?? '?'} ${item.name || item.id}`}
              />
            </Section>
          )}

          {isRoute && diff.itemsReordered.length > 0 && (
            <Section title="Sequence / position changes" icon={Shuffle} tone="warning">
              <CappedList
                items={diff.itemsReordered}
                render={(item) =>
                  item.moved
                    ? `${item.name || item.id} · pin moved`
                    : `${item.name || item.id} · #${item.from ?? '?'} → #${item.to ?? '?'}`
                }
              />
            </Section>
          )}
        </div>
      )}
    </Modal>
  );
}

function SnapshotCard({ tone, title, version, count, isRoute }) {
  const accent =
    tone === 'after'
      ? 'border-brand-200 bg-brand-50'
      : 'border-slate-200 bg-slate-50';
  return (
    <div className={`rounded-xl border p-3 ${accent}`}>
      <p className="text-[10.5px] uppercase tracking-wide text-slate-500">{title}</p>
      <p className="mt-1 text-[18px] font-semibold text-slate-900 tabular-nums">
        {version != null ? `V${version}` : '—'}
      </p>
      {isRoute ? (
        <p className="text-[11.5px] text-slate-500 mt-0.5 tabular-nums">{count ?? 0} stops</p>
      ) : null}
    </div>
  );
}

function Section({ title, icon: Icon, tone = 'default', children }) {
  const toneClass = {
    default: 'text-slate-500',
    success: 'text-emerald-600',
    danger: 'text-rose-600',
    warning: 'text-amber-600'
  }[tone];
  return (
    <div className="rounded-lg border border-slate-100 bg-white">
      <div className="flex items-center gap-1.5 px-3 pt-2.5 pb-1.5">
        {Icon ? <Icon className={`h-3.5 w-3.5 ${toneClass}`} /> : null}
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      </div>
      <ul className="px-3 pb-2.5 space-y-1">{children}</ul>
    </div>
  );
}

function CappedList({ items, render }) {
  const shown = items.slice(0, LIST_CAP);
  const extra = items.length - shown.length;
  return (
    <>
      {shown.map((item, i) => (
        <li key={item.id || i} className="text-[12.5px] text-slate-700 truncate">
          {render(item)}
        </li>
      ))}
      {extra > 0 && (
        <li className="text-[12px] text-slate-400 italic">
          <Badge variant="outline" size="sm">
            …and {extra} more
          </Badge>
        </li>
      )}
    </>
  );
}
